import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon,
  CalendarToday as CalendarTodayIcon,
  AttachMoney as AttachMoneyIcon,
  Warning as WarningIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  Info as InfoIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';
import indexedDB from '../../services/indexedDB';

const MyCollections = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [installments, setInstallments] = useState([]);
  const [loanBreakdowns, setLoanBreakdowns] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState({ totalDue: 0, totalArrears: 0, count: 0, totalPayable: 0 });
  const [expandedCustomers, setExpandedCustomers] = useState(new Set());
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [nicFilter, setNicFilter] = useState('');
  const [loanIdFilter, setLoanIdFilter] = useState('');

  useEffect(() => {
    fetchData();
    
    // Listen for online/offline events
    const handleOnline = () => {
      setIsOffline(false);
      fetchData();
    };
    const handleOffline = () => {
      setIsOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [date, nicFilter, loanIdFilter]);

  const fetchData = async () => {
    if (!user?.userId) return;
    
    // If offline, show empty with message
    if (!navigator.onLine) {
      setLoading(false);
      setError('You are currently offline. This page requires an internet connection.');
      setInstallments([]);
      setSummary({ totalDue: 0, totalArrears: 0, count: 0, totalPayable: 0 });
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      const params = {
        collectorId: user.userId,
        date,
      };
      
      // Add optional filters
      if (nicFilter && nicFilter.trim() !== '') {
        params.customerNic = nicFilter.trim();
      }
      if (loanIdFilter && loanIdFilter.trim() !== '') {
        params.loanId = loanIdFilter.trim();
      }
      
      const response = await api.get('/collector/installments/to-collect', { params });

      const data = response.data || {};
      const items = data.installments || [];
      setInstallments(items);
      setLoanBreakdowns(data.loanBreakdowns || {});
      
      // Calculate total payable from loan breakdowns
      // Total Payable = sum of all loan breakdowns' totalPayable (includes overdue + interest + current day)
      let totalPayable = 0;
      const breakdowns = data.loanBreakdowns || {};
      Object.values(breakdowns).forEach((breakdown) => {
        if (breakdown && breakdown.totalPayable) {
          totalPayable += Number(breakdown.totalPayable) || 0;
        }
      });
      
      setSummary({
        totalDue: data.totalDue || 0,
        totalArrears: data.totalArrears || 0,
        count: data.count || items.length,
        totalPayable: totalPayable,
      });
    } catch (err) {
      console.error('Error fetching installments to collect:', err);
      
      // If network error and offline, show appropriate message
      if (!navigator.onLine || err.code === 'ERR_NETWORK' || err.message.includes('Network')) {
        setError('You are currently offline. This page requires an internet connection.');
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to load data');
      }
      setInstallments([]);
      setSummary({ totalDue: 0, totalArrears: 0, count: 0, totalPayable: 0 });
    } finally {
      setLoading(false);
    }
  };

  // Group installments by customer
  const groupedByCustomer = installments.reduce((acc, inst) => {
    const key = `${inst.customerId}-${inst.customerNic}`;
    if (!acc[key]) {
      acc[key] = {
        customerId: inst.customerId,
        customerName: inst.customerName,
        customerNic: inst.customerNic,
        installments: [],
        totalDue: 0,
        totalArrears: 0,
        loanIds: new Set(),
      };
    }
    acc[key].installments.push(inst);
    acc[key].totalDue += Number(inst.amountDue || 0);
    acc[key].totalArrears += Number(inst.arrearsAmount || 0);
    acc[key].loanIds.add(inst.loanId);
    return acc;
  }, {});

  const toggleCustomer = (customerKey) => {
    const newExpanded = new Set(expandedCustomers);
    if (newExpanded.has(customerKey)) {
      newExpanded.delete(customerKey);
    } else {
      newExpanded.add(customerKey);
    }
    setExpandedCustomers(newExpanded);
  };

  const formatCurrency = (val) => {
    const num = Number(val || 0);
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const StatCard = ({ title, value, icon, gradient, subtitle, color }) => (
    <Card
      sx={{
        background: gradient || `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
        color: 'white',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        borderRadius: 3,
        transition: 'all 0.3s ease',
        height: '100%',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.25)',
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              {value}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading && installments.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">Loading due installments...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '100%', overflowX: 'hidden' }}>
      {isOffline && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          You are currently offline. This page requires an internet connection to load data.
        </Alert>
      )}
      
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{ 
            fontWeight: 700,
            color: '#0D47A1',
            mb: 1,
            fontSize: { xs: '1.75rem', sm: '2.25rem' },
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <CalendarTodayIcon sx={{ fontSize: { xs: 28, sm: 36 } }} />
          Due Today
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View and manage installments due for collection on the selected date
        </Typography>
      </Box>

      {/* Summary Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Due"
            value={formatCurrency(summary.totalPayable > 0 ? summary.totalPayable : summary.totalDue)}
            icon={<AttachMoneyIcon sx={{ fontSize: { xs: 28, sm: 36 } }} />}
            gradient="linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)"
            subtitle={summary.totalPayable > 0 
              ? `${summary.count} installment(s) (includes overdue + interest + current day)`
              : `${summary.count} installment(s)`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Arrears"
            value={formatCurrency(summary.totalArrears)}
            icon={<WarningIcon sx={{ fontSize: { xs: 28, sm: 36 } }} />}
            gradient="linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)"
            subtitle="Overdue amounts"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Customers"
            value={Object.keys(groupedByCustomer).length}
            icon={<PersonIcon sx={{ fontSize: { xs: 28, sm: 36 } }} />}
            gradient="linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)"
            subtitle="With due installments"
          />
        </Grid>
      </Grid>

      {/* Filters Section */}
      <Paper 
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          mb: 3, 
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(13, 71, 161, 0.1)',
          background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(13, 71, 161, 0.05) 100%)',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#0D47A1' }}>
          Filters & Search
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Select Date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarTodayIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Filter by NIC"
              value={nicFilter}
              onChange={(e) => setNicFilter(e.target.value)}
              placeholder="Enter customer NIC"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: nicFilter && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setNicFilter('')}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Filter by Loan ID"
              value={loanIdFilter}
              onChange={(e) => setLoanIdFilter(e.target.value)}
              placeholder="Enter loan ID"
              InputLabelProps={{ shrink: true }}
              type="number"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountBalanceIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: loanIdFilter && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setLoanIdFilter('')}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={fetchData}
              disabled={loading}
              sx={{
                height: '56px',
                background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1565C0 0%, #0A3D91 100%)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Refresh'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {loading && installments.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      {/* Customers List */}
      {installments.length === 0 && !loading ? (
        <Paper 
          sx={{ 
            p: 6, 
            textAlign: 'center',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          }}
        >
          <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Installments Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No installments are due for collection on the selected date.
          </Typography>
        </Paper>
      ) : (
        Object.keys(groupedByCustomer).map((customerKey) => {
          const customer = groupedByCustomer[customerKey];
          const isExpanded = expandedCustomers.has(customerKey);
          const loanIdsArray = Array.from(customer.loanIds);
          
          return (
            <Card 
              key={customerKey} 
              sx={{ 
                mb: 3,
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Customer Header */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    cursor: 'pointer',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                  }}
                  onClick={() => toggleCustomer(customerKey)}
                >
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton 
                      size="small"
                      sx={{ 
                        backgroundColor: 'primary.light',
                        color: 'primary.main',
                        '&:hover': { backgroundColor: 'primary.main', color: 'white' },
                      }}
                    >
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {customer.customerName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip 
                          label={`NIC: ${customer.customerNic}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                        <Chip 
                          label={`${customer.installments.length} Installment(s)`}
                          size="small"
                          color="primary"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
                      {formatCurrency(customer.totalDue)}
                    </Typography>
                    {customer.totalArrears > 0 && (
                      <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
                        Arrears: {formatCurrency(customer.totalArrears)}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Collapse in={isExpanded}>
                  <Divider sx={{ my: 3 }} />
                  
                  {/* Installments Table */}
                  <TableContainer 
                    component={Paper} 
                    variant="outlined" 
                    sx={{ 
                      mb: 3,
                      borderRadius: 2,
                      overflowX: 'auto',
                      '& .MuiTableHead-root': {
                        backgroundColor: 'primary.light',
                      },
                    }}
                  >
                    <Table size={isMobile ? 'small' : 'medium'}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, color: 'primary.dark' }}>Loan ID</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'primary.dark' }}>Installment #</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'primary.dark' }}>Due Date</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'primary.dark' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'primary.dark' }}>Base Amount</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'primary.dark' }}>Late Interest</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'primary.dark' }}>Total Due</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'primary.dark' }}>Total Payable</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'primary.dark' }}>Arrears</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {customer.installments.map((inst) => {
                          const isOverdue = new Date(inst.dueDate) < new Date(date);
                          return (
                            <TableRow 
                              key={inst.installmentId}
                              sx={{
                                '&:hover': { backgroundColor: 'action.hover' },
                                backgroundColor: isOverdue ? 'error.light' : 'transparent',
                              }}
                            >
                              <TableCell>
                                <Chip 
                                  label={`#${inst.loanId}`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>{inst.installmentNumber}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  {new Date(inst.dueDate).toLocaleDateString()}
                                  {isOverdue && (
                                    <Chip 
                                      label="Overdue" 
                                      color="error" 
                                      size="small"
                                      icon={<WarningIcon />}
                                    />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={inst.status} 
                                  size="small"
                                  color={
                                    inst.status === 'PAID' ? 'success' :
                                    inst.status === 'PARTIAL' ? 'warning' :
                                    inst.status === 'OVERDUE' || inst.status === 'LATE' ? 'error' : 'default'
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {formatCurrency(inst.baseAmount || 0)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {inst.lateInterest > 0 ? (
                                  <Typography variant="body2" color="error" sx={{ fontWeight: 700 }}>
                                    {formatCurrency(inst.lateInterest)}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                  {formatCurrency(inst.amountDue)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {inst.totalPayable && inst.totalPayable > 0 ? (
                                  <Tooltip
                                    title={
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                          Total Payable Breakdown for {new Date(inst.dueDate).toLocaleDateString()}:
                                        </Typography>
                                        {(() => {
                                          const breakdown = loanBreakdowns[inst.loanId];
                                          if (breakdown) {
                                            return (
                                              <>
                                                <Typography variant="caption" component="div">
                                                  Overdue Base: {formatCurrency(breakdown.totalOverdueBase || 0)}
                                                </Typography>
                                                <Typography variant="caption" component="div">
                                                  Late Interest: {formatCurrency(breakdown.totalLateInterest || 0)}
                                                </Typography>
                                                <Typography variant="caption" component="div">
                                                  Current Day: {formatCurrency(breakdown.currentDayBase || 0)}
                                                </Typography>
                                              </>
                                            );
                                          }
                                          return null;
                                        })()}
                                      </Box>
                                    }
                                    arrow
                                  >
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        fontWeight: 700, 
                                        color: 'success.main',
                                        cursor: 'help',
                                        textDecoration: 'underline',
                                        textDecorationStyle: 'dotted',
                                      }}
                                    >
                                      {formatCurrency(inst.totalPayable)}
                                    </Typography>
                                  </Tooltip>
                                ) : (
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                    {formatCurrency(inst.amountDue)}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {inst.arrearsAmount > 0 ? (
                                  <Typography variant="body2" color="error" sx={{ fontWeight: 700 }}>
                                    {formatCurrency(inst.arrearsAmount)}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Total Payable Breakdown for each loan */}
                  {loanIdsArray.map((loanId) => {
                    const breakdown = loanBreakdowns[loanId];
                    if (!breakdown) return null;
                    
                    return (
                      <Card 
                        key={loanId} 
                        sx={{ 
                          mb: 3,
                          background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(13, 71, 161, 0.05) 100%)',
                          border: '2px solid',
                          borderColor: 'primary.main',
                          borderRadius: 3,
                        }}
                      >
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                            <AccountBalanceIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                              Loan #{loanId} - Total Payable Breakdown
                            </Typography>
                          </Box>
                          
                          {/* Overdue Installments */}
                          {breakdown.overdueInstallments > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'error.main' }}>
                                <WarningIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                Overdue Installments ({breakdown.overdueInstallments})
                              </Typography>
                              <Paper sx={{ p: 2, backgroundColor: 'error.light', borderRadius: 2 }}>
                                {breakdown.overdueDetails && breakdown.overdueDetails.map((detail, idx) => (
                                  <Box key={idx} sx={{ mb: 1, pl: 2, borderLeft: '3px solid', borderColor: 'error.main' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      Installment #{detail.installmentNumber} (Due: {new Date(detail.dueDate).toLocaleDateString()})
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                                      <Typography variant="body2" color="text.secondary">
                                        Base: {formatCurrency(detail.baseAmount || 0)}
                                      </Typography>
                                      <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
                                        Interest: {formatCurrency(detail.lateInterest || 0)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                ))}
                                <Divider sx={{ my: 1.5 }} />
                                <Typography variant="body1" sx={{ fontWeight: 700, color: 'error.dark' }}>
                                  Total Overdue Base: {formatCurrency(breakdown.totalOverdueBase || 0)}
                                </Typography>
                              </Paper>
                            </Box>
                          )}
                          
                          {/* Interest Calculation */}
                          {breakdown.totalLateInterest > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'error.main' }}>
                                <TrendingUpIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                Late Interest Calculation
                              </Typography>
                              <Paper sx={{ p: 2.5, backgroundColor: 'error.light', borderRadius: 2, border: '1px solid', borderColor: 'error.main' }}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Interest Rate:</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.dark' }}>
                                      {parseFloat(breakdown.interestRate || 0).toFixed(2)}%
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Total Overdue Base:</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.dark' }}>
                                      {formatCurrency(breakdown.totalOverdueBase || 0)}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                      Calculation Formula:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', backgroundColor: 'rgba(0,0,0,0.05)', p: 1, borderRadius: 1 }}>
                                      {formatCurrency(breakdown.totalOverdueBase || 0)} × {parseFloat(breakdown.interestRate || 0).toFixed(2)}% = {formatCurrency(breakdown.totalLateInterest || 0)}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Paper>
                            </Box>
                          )}
                          
                          {/* Current Day Installment */}
                          {breakdown.hasCurrentDayInstallment && breakdown.currentDayDetail && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
                                <CalendarTodayIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                Current Day Installment ({new Date(breakdown.targetDate).toLocaleDateString()})
                              </Typography>
                              <Paper sx={{ p: 2, backgroundColor: 'primary.light', borderRadius: 2 }}>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  Installment #{breakdown.currentDayDetail.installmentNumber}
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark', mt: 1 }}>
                                  Base Amount: {formatCurrency(breakdown.currentDayDetail.baseAmount || 0)}
                                </Typography>
                              </Paper>
                            </Box>
                          )}
                          
                          {/* Total Payable */}
                          <Paper 
                            sx={{ 
                              p: 3, 
                              background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
                              color: 'white',
                              borderRadius: 3,
                              boxShadow: '0 4px 20px rgba(46, 125, 50, 0.3)',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <PaymentIcon sx={{ fontSize: 32 }} />
                              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                Total Payable
                              </Typography>
                            </Box>
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
                              {formatCurrency(breakdown.totalPayable || 0)}
                            </Typography>
                            <Divider sx={{ my: 2, backgroundColor: 'rgba(255,255,255,0.3)' }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                = {formatCurrency(breakdown.totalOverdueBase || 0)} (Overdue Base)
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                + {formatCurrency(breakdown.totalLateInterest || 0)} (Interest)
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                + {formatCurrency(breakdown.currentDayBase || 0)} (Current Day)
                              </Typography>
                            </Box>
                          </Paper>
                        </CardContent>
                      </Card>
                    );
                  })}
                  
                  {/* View Loan Schedule Buttons */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 3 }}>
                    {loanIdsArray.map((loanId) => (
                      <Button
                        key={loanId}
                        variant="contained"
                        size="large"
                        startIcon={<DescriptionIcon />}
                        onClick={() => navigate(`/collector/field-officer?loanId=${loanId}`)}
                        sx={{
                          background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #1565C0 0%, #0A3D91 100%)',
                          },
                          textTransform: 'none',
                          borderRadius: 2,
                          px: 3,
                        }}
                      >
                        View Loan #{loanId} Schedule
                      </Button>
                    ))}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          );
        })
      )}
    </Box>
  );
};

export default MyCollections;
